import { useEffect, useState } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { cn } from "@/lib/utils";

export interface Chapter {
  time: string;
  title: string;
  title_es?: string;
  description?: string;
}

interface WebinarChaptersProps {
  chapters: Chapter[];
}

const toSeconds = (t: string) => {
  const parts = t.split(":").map((p) => parseInt(p, 10));
  if (parts.some((n) => Number.isNaN(n))) return null;
  return parts.reduce((acc, n) => acc * 60 + n, 0);
};

const WebinarChapters = ({ chapters }: WebinarChaptersProps) => {
  const lang = useLanguage();
  const isEs = lang === "es";
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    if (!chapters?.length) return;
    const starts = chapters.map((c) => toSeconds(c.time) ?? 0);
    let video: HTMLVideoElement | null = null;
    const onTime = () => {
      if (!video) return;
      const t = video.currentTime;
      let idx = -1;
      starts.forEach((s, i) => { if (t >= s) idx = i; });
      setActive(idx >= 0 && (t > 0 || !video.paused) ? idx : null);
    };
    const attach = () => {
      video = document.querySelector<HTMLVideoElement>("#webinar-player video");
      if (!video) return false;
      video.addEventListener("timeupdate", onTime);
      video.addEventListener("seeked", onTime);
      return true;
    };
    let timer: number | undefined;
    if (!attach()) timer = window.setInterval(() => { if (attach()) window.clearInterval(timer); }, 500);
    return () => {
      if (timer) window.clearInterval(timer);
      video?.removeEventListener("timeupdate", onTime);
      video?.removeEventListener("seeked", onTime);
    };
  }, [chapters]);

  if (!chapters || chapters.length === 0) return null;

  const seek = (time: string) => {
    const seconds = toSeconds(time);
    if (seconds === null) return;
    const video = document.querySelector<HTMLVideoElement>("#webinar-player video");
    if (!video) return;
    video.currentTime = seconds;
    video.play().catch(() => {});
    video.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section className="py-10 sm:py-14 bg-background">
      <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
        <p className="text-xs font-bold uppercase tracking-wider text-accent mb-2">
          {isEs ? "Contenido" : "Contents"}
        </p>
        <h2 className="text-xl font-bold text-foreground mb-6">
          {isEs ? "Capítulos" : "Chapters"}
        </h2>
        <ol className="border-l border-border/60 ml-1 space-y-0">
          {chapters.map((chapter, i) => {
            const isActive = active === i;
            return (
              <li key={i} className="-ml-px">
                <button
                  type="button"
                  onClick={() => seek(chapter.time)}
                  aria-current={isActive ? "true" : undefined}
                  className={cn(
                    "relative w-full text-left pl-6 pr-3 py-3 group hover:bg-secondary/40 transition-colors rounded-r-lg",
                    isActive && "bg-secondary/60"
                  )}
                >
                  <span
                    className={cn(
                      "absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-border group-hover:bg-accent transition-colors",
                      isActive && "bg-accent w-2.5 h-2.5"
                    )}
                  />
                  <span className="flex items-baseline gap-3">
                    <span
                      className={cn(
                        "text-xs font-mono text-muted-foreground tabular-nums shrink-0 group-hover:text-accent transition-colors",
                        isActive && "text-accent font-semibold"
                      )}
                    >
                      {chapter.time}
                    </span>
                    <span className={cn("text-sm font-medium text-foreground", isActive && "font-semibold")}>
                      {isEs ? (chapter.title_es || chapter.title) : chapter.title}
                    </span>
                  </span>
                  {chapter.description && (
                    <span className="block mt-0.5 pl-[calc(theme(spacing.3)+3.5ch)] text-xs text-muted-foreground leading-relaxed">
                      {chapter.description}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

export default WebinarChapters;
